import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router";

import { passkeysSupported, registerWithPasskey, registerWithPassword } from "../../api/auth";
import type { Registered } from "../../api/auth";
import { ApiError, errorText } from "../../api/client";
import { Button } from "../../components/Button";
import { nl } from "../../i18n/nl";
import { AuthLayout, Field, FormError } from "./AuthLayout";
import styles from "./auth.module.css";
import { RecoveryCodes } from "./RecoveryCodes";
import { useRefreshSession } from "./session";

const t = nl.auth;
type Step = "email" | "code" | "method" | "codes";
const STEP_NUMBER: Record<Exclude<Step, "codes">, number> = { email: 1, code: 2, method: 3 };

/** Which step a server error belongs to, so the student lands where the fix is. */
const STEP_FOR_ERROR: Record<string, Step> = {
  invalid_domain: "email",
  invalid_email: "email",
  email_taken: "email",
  invalid_invite_code: "code",
};

export function RegisterPage() {
  const navigate = useNavigate();
  const refresh = useRefreshSession();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Partial<Record<Step | "password" | "form", string>>>({});
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState<string[]>([]);
  const supportsPasskeys = passkeysSupported();
  const formRef = useRef<HTMLDivElement>(null);

  // Move focus to the first field of the new step (keyboard and screen reader users).
  useEffect(() => {
    if (step === "email") return;
    formRef.current?.querySelector<HTMLInputElement>("input, button")?.focus();
  }, [step]);

  async function finish(action: () => Promise<Registered>) {
    setBusy(true);
    setErrors({});
    try {
      const result = await action();
      await refresh();
      setCodes(result.recoveryCodes);
      setStep("codes");
    } catch (failure) {
      const errorCode = failure instanceof ApiError ? failure.code : "";
      const target = STEP_FOR_ERROR[errorCode];
      if (target) {
        setStep(target);
        setErrors({ [target]: errorText(failure) });
      } else if (errorCode === "weak_password") {
        setErrors({ password: errorText(failure) });
      } else {
        setErrors({ form: errorText(failure) });
      }
    } finally {
      setBusy(false);
    }
  }

  function next(event: FormEvent) {
    event.preventDefault();
    if (step === "email") {
      if (!email.includes("@")) {
        setErrors({ email: email.trim() ? nl.errors.invalid_email : t.required });
        return;
      }
      setErrors({});
      setStep("code");
    } else if (step === "code") {
      if (!code.trim()) {
        setErrors({ code: t.required });
        return;
      }
      setErrors({});
      setStep("method");
    }
  }

  if (step === "codes") {
    return (
      <AuthLayout>
        <RecoveryCodes
          codes={codes}
          onDone={() => {
            void navigate("/welkom", { replace: true });
          }}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div ref={formRef}>
      <p className={styles.kicker}>{t.register.stepOf(STEP_NUMBER[step], 3)}</p>
      <h1 className={styles.title}>
        {step === "method" ? t.register.methodTitle : t.register.title}
      </h1>

      {step !== "method" ? (
        <form className={styles.form} onSubmit={next} noValidate>
          {step === "email" ? (
            <Field
              key="email"
              label={t.email}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              spellCheck={false}
              placeholder={t.emailPlaceholder}
              help={t.emailHelp}
              value={email}
              error={errors.email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          ) : (
            <Field
              key="code"
              label={t.register.inviteCode}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder={t.register.inviteCodePlaceholder}
              help={t.register.inviteCodeHelp}
              value={code}
              error={errors.code}
              onChange={(event) => {
                setCode(event.target.value);
              }}
            />
          )}
          <Button type="submit" variant="primary" size="large" className={styles.full}>
            {t.register.next}
          </Button>
          {step === "code" && (
            <Button
              variant="ghost"
              onClick={() => {
                setStep("email");
              }}
            >
              {t.register.back}
            </Button>
          )}
        </form>
      ) : (
        <div className={styles.form}>
          <FormError message={errors.form ?? null} />
          {supportsPasskeys ? (
            <section className={styles.choice} aria-labelledby="passkey-title">
              <h2 id="passkey-title" className={styles.choiceTitle}>
                {t.register.passkeyTitle}
                <span className={styles.badge}>{t.register.passkeyRecommended}</span>
              </h2>
              <p className={styles.choiceText}>{t.register.passkeyText}</p>
              <Button
                variant="primary"
                size="large"
                disabled={busy}
                onClick={() => {
                  void finish(() => registerWithPasskey(email, code));
                }}
              >
                {t.register.passkeyButton}
              </Button>
            </section>
          ) : (
            <p className={styles.help}>{t.register.noPasskeySupport}</p>
          )}
          <form
            className={styles.choice}
            aria-labelledby="password-title"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (!password) {
                setErrors({ password: t.required });
                return;
              }
              void finish(() => registerWithPassword(email, code, password));
            }}
          >
            <h2 id="password-title" className={styles.choiceTitle}>
              {t.register.passwordTitle}
            </h2>
            <Field
              label={t.password}
              type="password"
              autoComplete="new-password"
              help={t.passwordHelp}
              value={password}
              error={errors.password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
            <Button
              type="submit"
              variant={supportsPasskeys ? "secondary" : "primary"}
              size="large"
              disabled={busy}
            >
              {busy ? t.working : t.register.passwordButton}
            </Button>
          </form>
          <Button
            variant="ghost"
            onClick={() => {
              setStep("code");
            }}
          >
            {t.register.back}
          </Button>
        </div>
      )}

      </div>
      <div className={styles.links}>
        <Link to="/inloggen">
          {t.register.haveAccount} {t.login.title}
        </Link>
      </div>
    </AuthLayout>
  );
}
